import {
  DefaultKeyToStorage,
  OwnerIdKeyToStorage,
  TokenStorage,
  getLocalStorage,
} from 'shared/libs/storage'
import { trackAdjustEventAddToCart } from 'shared/metrics/adjust/adjust'
import { useAppDispatch, useAppSelector } from 'shared/store'

import {
  useLazyGetAuthCartByIdQuery,
  useLazyGetAuthCartsQuery,
  useLazyGetTemporaryCartByIdQuery,
  useLazyGetTemporaryCartsQuery,
  useMutateAuthCartMutation,
  useMutateTemporaryCartMutation,
  useRemoveAuthCartMutation,
  useRemoveProductFromAuthCartMutation,
  useRemoveProductFromTemporaryCartMutation,
  useRemoveTemporaryCartMutation,
} from 'modules/Cart/model/api'
import { CartSliceActions } from 'modules/Cart/model/cart.slice'
import { Cart, MutateCart } from 'modules/Cart/model/types'

interface ActionConfig {
  onSuccess?: (result?: any) => void
  onError?: (error: unknown) => void
  onLoad?: (val: boolean) => void
}

export function useCart() {
  const ownerId = getLocalStorage(OwnerIdKeyToStorage)
  const token = TokenStorage.getFromStorage(DefaultKeyToStorage)
  const dispatch = useAppDispatch()

  const cartById = useAppSelector(state => state.cart?.cartById)
  const cartList = useAppSelector(state => state.cart?.cartList)
  const totalProductsCount = useAppSelector(
    state => state.cart?.totalProductsCount,
  )

  const [mutateAuthCart] = useMutateAuthCartMutation()
  const [mutateTemporaryCart] = useMutateTemporaryCartMutation()

  const [getAuthCartById] = useLazyGetAuthCartByIdQuery()
  const [getTemporaryCartById] = useLazyGetTemporaryCartByIdQuery()

  const [getTemporaryCarts] = useLazyGetTemporaryCartsQuery()
  const [getAuthCarts] = useLazyGetAuthCartsQuery()

  const [removeTemporaryCart] = useRemoveTemporaryCartMutation()
  const [removeAuthCart] = useRemoveAuthCartMutation()

  const [removeProductFromAuthCart] = useRemoveProductFromAuthCartMutation()
  const [removeProductFromTemporaryCart] =
    useRemoveProductFromTemporaryCartMutation()

  const clearCart = (cartPk: Cart['pk'], config?: ActionConfig) => {
    if (config?.onLoad) config.onLoad(true)
    const removeCart = token
      ? removeAuthCart(cartPk)
      : removeTemporaryCart({ cartPk, ownerId })
    removeCart
      .unwrap()
      .then(() => {
        dispatch(CartSliceActions.removeCart(cartPk))
        if (config?.onSuccess) config.onSuccess()
      })
      .finally(() => {
        if (config?.onLoad) config.onLoad(false)
      })
  }

  const getCarts = (config?: ActionConfig) => {
    if (token || ownerId) {
      if (config?.onLoad) config.onLoad(true)
      const getCartList = token ? getAuthCarts() : getTemporaryCarts(ownerId)
      getCartList
        .unwrap()
        .then(res => {
          dispatch(CartSliceActions.setCartList(res))
        })
        .finally(() => {
          if (config?.onLoad) config.onLoad(false)
        })
    }
  }

  const getCartById = (cartPk: Cart['pk'], config?: ActionConfig) => {
    if (config?.onLoad) config.onLoad(true)
    const getCart = token
      ? getAuthCartById(cartPk)
      : getTemporaryCartById({ cartPk, ownerId })
    getCart
      .unwrap()
      .then(res => {
        dispatch(CartSliceActions.setCartById(res))
      })
      .then(() => {
        if (config?.onLoad) config.onLoad(false)
      })
  }

  const removeProductFromCart = (id: number, config?: ActionConfig) => {
    if (config?.onLoad) config.onLoad(true)
    const removeProduct = token
      ? removeProductFromAuthCart(id)
      : removeProductFromTemporaryCart({ ownerId, id })
    removeProduct
      .unwrap()
      .then(res => {
        dispatch(CartSliceActions.removeProduct(res))
        if (config?.onSuccess) config.onSuccess()
      })
      .finally(() => {
        if (config?.onLoad) config.onLoad(false)
      })
  }

  const mutateProductInCart = (
    { product, quantity }: MutateCart,
    config?: ActionConfig,
  ) => {
    if (config?.onLoad) config.onLoad(true)
    const mutateProduct = token
      ? mutateAuthCart({ product, quantity })
      : mutateTemporaryCart({ ownerId, product, quantity })
    mutateProduct
      .unwrap()
      .then(res => {
        dispatch(CartSliceActions.mutateProduct(res))
        if (config?.onSuccess) config.onSuccess(res)
        try {
          if (window.initAdjust === true) {
            trackAdjustEventAddToCart()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('trackAdjustEventAddToCart error:', error)
        }
      })
      .finally(() => {
        if (config?.onLoad) config.onLoad(false)
      })
  }

  const clearAllCartStates = () => {
    dispatch(CartSliceActions.clearAllStates())
  }

  return {
    cartList,
    totalProductsCount,
    cartById,
    clearCart,
    getCarts,
    getCartById,
    removeProductFromCart,
    mutateProductInCart,
    clearAllCartStates,
  }
}
